import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { notifications as notifApi, users as usersApi } from "../api/endpoints";
import { useCaseLauncher } from "../components/CaseLauncher";
import Modal from "../components/Modal";
import Spinner from "../components/Spinner";
import { useToast } from "../components/Toast";

// feature/notif-icons. Distinct icon per notification type
// so the bell preview is scannable. Falls back to 🔔 if type is unknown.
const NOTIF_TYPE_ICONS = {
  "case.insumo_received":            "📩",
  "case_returned":                   "🔁",
  "case.devuelto_resuelto":          "🔁",
  "case.contestada_stop_work":       "🛑",
  "case.moderation_requested":       "⚠️",
  "case.moderation_resolved":        "✅",
  "case.sancion_inaplicacion":       "🚨",
  "case.marked_contestada":          "📨",
  "case_assigned":                   "⚖️",
  "case_created":                    "🆕",
  "insumo_assigned":                 "📋",
  "insumo_response":                 "📝",
  "insumo_stolen":                   "↪️",
  "audit_pending":                   "🔍",
  "audit_approved":                  "✅",
  "audit_rejected":                  "❌",
  "fallo_pending":                   "📂",
  // feature/deadline-nudges — sprint88. ⏰ approaching, ⏳ breached.
  "deadline.lawyer_t2h":             "⏰",
  "deadline.insumos_t2h":           "⏰",
  "deadline.lawyer_breach":         "⏳",
  "deadline.insumos_breach":        "⏳",
  "deadline.breach_oversight":      "⏳",
};

const ROLE_LABEL = {
  admin: "Administrador",
  reparto: "Reparto / Auditoría",
  insumos: "Insumos",
  reparto_insumos: "Reparto de insumos",
  revisor_fallos: "Revisor de fallos",
  abogado: "Abogado",
  directora: "Directora",
  auditor: "Auditor",
};

// feature/notion-chrome — glass top bar; layout via CSS classes (see index.css).
const styles = {
  iconBtn: {
    background: "transparent", border: "none", boxShadow: "none",
    color: "var(--text-body)", padding: 8, height: 36, width: 36,
    borderRadius: 8, cursor: "pointer", position: "relative", marginRight: 8,
  },
  badge: {
    position: "absolute", top: 4, right: 4,
    background: "var(--color-danger-bg)", color: "var(--color-danger)",
    fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 999,
    lineHeight: 1.2, minWidth: 16, textAlign: "center",
  },
  avatar: {
    width: 28, height: 28, borderRadius: "50%",
    background: "var(--accent-mint-bg)", color: "var(--accent-mint)",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontWeight: 600, fontSize: 11,
  },
  userName: { fontSize: 13, fontWeight: 500, color: "var(--text-primary)" },
  userRole: { fontSize: 11, color: "var(--text-muted)" },
};

function timeAgo(d) {
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

export default function TopBar() {
  const { user, logout, setTheme } = useAuth();
  const nav = useNavigate();
  const launcher = useCaseLauncher();
  const toast = useToast();
  const isDark = (user?.theme || "light") === "dark";

  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const lastIdsRef = useRef(new Set());
  const userRef = useRef(null);
  const notifRef = useRef(null);

  // feature/smart-refresh — when poll returns NEW notifications, fan out a CustomEvent
  // that view components listen for and re-fetch their data without a page
  // reload. Keeps scroll position, preserves textarea content (the listeners
  // are expected to defer if document.activeElement is inside them).
  const load = useCallback((isInitial = false) => {
    if (!user) return;
    notifApi.list().then(r => {
      const next = r.data || [];
      const prev = lastIdsRef.current;
      if (!isInitial) {
        const newOnes = next.filter(n => !prev.has(n.id));
        if (newOnes.length) {
          const types = Array.from(new Set(newOnes.map(n => n.type)));
          window.dispatchEvent(new CustomEvent("eds:refresh", { detail: { types } }));
          toast.push("Nueva actualización", "success");
        }
      }
      lastIdsRef.current = new Set(next.map(n => n.id));
      setItems(next);
    }).catch(() => {});
  }, [user, toast]);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 60_000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    function onClick(e) {
      if (userRef.current && !userRef.current.contains(e.target)) setOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;
  const initials = (user.name || "?").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const unread = items.filter(n => n.read_at == null).length;

  // feature/notifications-clear — optimistic UI: flip the local state immediately, fire the
  // backend update in the background, revert + toast on failure.
  async function openNotif(n) {
    setNotifOpen(false);
    if (n.read_at == null) {
      const prev = n.read_at;
      const ts = new Date().toISOString();
      setItems(arr => arr.map(x => x.id === n.id ? { ...x, read_at: ts } : x));
      try { await notifApi.read(n.id); }
      catch {
        setItems(arr => arr.map(x => x.id === n.id ? { ...x, read_at: prev } : x));
      }
    }
    if (n.case_id) launcher.open(n.case_id);
  }

  async function dismissOne(id, e) {
    if (e) e.stopPropagation();
    const prev = items.find(x => x.id === id)?.read_at;
    if (prev != null) return; // already read
    const ts = new Date().toISOString();
    setItems(arr => arr.map(x => x.id === id ? { ...x, read_at: ts } : x));
    try { await notifApi.read(id); }
    catch {
      setItems(arr => arr.map(x => x.id === id ? { ...x, read_at: prev } : x));
      toast.push("No se pudo marcar como leída", "error");
    }
  }

  async function markAll() {
    const ts = new Date().toISOString();
    const snapshot = items.map(x => x.read_at);
    setItems(arr => arr.map(x => ({ ...x, read_at: x.read_at || ts })));
    try { await notifApi.readAll(); }
    catch {
      setItems(arr => arr.map((x, i) => ({ ...x, read_at: snapshot[i] })));
      toast.push("No se pudo marcar todas como leídas", "error");
    }
  }

  function openCommandPalette() {
    const isMac = navigator.platform.toLowerCase().includes("mac");
    const ev = new KeyboardEvent("keydown", {
      key: "k", code: "KeyK",
      bubbles: true, cancelable: true,
      ctrlKey: !isMac, metaKey: isMac,
    });
    document.dispatchEvent(ev);
  }

  return (
    <header className="top-bar">
      <button className="topbar-search-hint" onClick={openCommandPalette}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
        </svg>
        <span>Buscar</span>
        <kbd>⌘K</kbd>
      </button>
      <div style={{ flex: 1 }} />

      <button
        style={styles.iconBtn}
        title={isDark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
        aria-label="Tema"
        onClick={() => setTheme(isDark ? "light" : "dark")}
      >
        {isDark ? (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
          </svg>
        )}
      </button>

      <div ref={notifRef} style={{ position: "relative" }}>
        <button style={styles.iconBtn} title="Notificaciones" aria-label="Notificaciones" onClick={() => { setNotifOpen(v => !v); load(); }}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 8a6 6 0 1 1 12 0c0 4 1.5 5.5 2 6.5H4c.5-1 2-2.5 2-6.5Z" />
            <path d="M10 18a2 2 0 0 0 4 0" />
          </svg>
          {unread > 0 && <span style={styles.badge}>{unread > 99 ? "99+" : unread}</span>}
        </button>

        {notifOpen && (
          <div className="topbar-notif-panel" role="menu">
            <div className="notif-dropdown-header">
              <span className="notif-dropdown-title">Notificaciones</span>
              {unread > 0 && (
                <button className="notif-mark-all-read" onClick={markAll}>
                  Marcar todas como leídas
                </button>
              )}
            </div>
            <div style={{ height: 1, background: "var(--glass-border)" }} />
            {/* feature/notifications-history. The dropdown still
                shows recent/unread items; "Ver todas" navigates to the full
                history page (filterable by category + period + read state). */}
            {!items.length ? (
              <>
                <div style={{ padding: 24, textAlign: "center" }} className="muted">Sin notificaciones</div>
                <div style={{
                  borderTop: "1px solid var(--glass-border)",
                  padding: "10px 16px", textAlign: "center",
                }}>
                  <button
                    className="ghost"
                    style={{ background: "none", border: "none", color: "var(--accent-mint)", fontWeight: 600, fontSize: 12, cursor: "pointer", padding: 0 }}
                    onClick={() => { setNotifOpen(false); nav("/notificaciones"); }}
                  >
                    Ver todas las notificaciones →
                  </button>
                </div>
              </>
            ) : (
              items.slice(0, 10).map(n => {
                const isUnread = n.read_at == null;
                // feature/notif-icons.
                const icon = NOTIF_TYPE_ICONS[n.type] || "🔔";
                return (
                  <div
                    key={n.id}
                    className={"notif-item " + (isUnread ? "is-unread" : "is-read")}
                    onClick={() => openNotif(n)}
                  >
                    <div className="notif-content" style={{ display: "flex", gap: 8 }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="notif-message">{n.message}</div>
                        <div className="notif-meta">
                          <span>{timeAgo(n.created_at)}</span>
                          {n.case_id && <span className="badge" style={{ padding: "0 8px" }}>Ver caso</span>}
                        </div>
                      </div>
                    </div>
                    {isUnread && (
                      <button
                        className="notif-dismiss"
                        title="Marcar como leída"
                        aria-label="Marcar como leída"
                        onClick={(e) => dismissOne(n.id, e)}
                      >×</button>
                    )}
                  </div>
                );
              })
            )}
            {/* feature/notifications-history footer. Always shown
                so users can reach the history page even when the dropdown
                already had items (10 visible cap). */}
            {items.length > 0 && (
              <div style={{
                borderTop: "1px solid var(--glass-border)",
                padding: "10px 16px", textAlign: "center",
              }}>
                <button
                  className="ghost"
                  style={{ background: "none", border: "none", color: "var(--accent-mint)", fontWeight: 600, fontSize: 12, cursor: "pointer", padding: 0 }}
                  onClick={() => { setNotifOpen(false); nav("/notificaciones"); }}
                >
                  Ver todas las notificaciones →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div ref={userRef} className="topbar-user" onClick={() => setOpen(v => !v)} role="button" aria-haspopup="menu">
        <div style={styles.avatar}>{initials}</div>
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
          <span style={styles.userName}>{user.name}</span>
          <span style={styles.userRole}>{ROLE_LABEL[user.role] || user.role}</span>
        </div>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--text-hint)" }}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>

      {open && (
        <div className="avatar-dropdown" role="menu">
          <div className="avatar-dropdown-meta">{user.email}</div>
          <div className="avatar-dropdown-divider" />
          <button
            className="avatar-dropdown-item"
            onClick={() => { setOpen(false); setPrefsOpen(true); }}
          >
            Preferencias de notificaciones
          </button>
          <button
            className="avatar-dropdown-item"
            onClick={() => { setTheme(isDark ? "light" : "dark"); }}
          >
            Cambiar a modo {isDark ? "claro" : "oscuro"}
          </button>
          <div className="avatar-dropdown-divider" />
          <button
            className="avatar-dropdown-item destructive"
            onClick={async () => { setOpen(false); await logout(); nav("/login"); }}
          >
            Cerrar sesión
          </button>
        </div>
      )}

      <PreferencesModal open={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </header>
  );
}

// feature/notification-prefs — notification preferences modal. Each toggle maps to a
// notification_prefs key on the user; default is "all on" (undefined → true).
const NOTIF_OPTIONS = [
  { key: "case_assigned",   label: "Casos asignados",                    desc: "Cuando le asignan un caso nuevo." },
  { key: "insumo_response", label: "Insumos recibidos",                  desc: "Cuando llegan insumos para un caso suyo." },
  { key: "audit_pending",   label: "Auditoría pendiente",                desc: "Cuando un abogado envía un caso a auditoría." },
  { key: "audit_decision",  label: "Decisión de auditor",                desc: "Cuando aprueban o devuelven su caso." },
  { key: "insumo_stolen",   label: "Caso reclamado por otro miembro",    desc: "Cuando otro miembro de Insumos reclama su caso." },
  { key: "case_returned",   label: "Cambios en mis casos",               desc: "Devueltos por insumos inválidos, resoluciones de directora." },
  { key: "fallo_pending",   label: "Fallos pendientes (Molly)",          desc: "Cuando hay fallos esperando su decisión." },
  // feature/deadline-nudges — sprint88.
  { key: "deadline_nudges", label: "Avisos de plazos",                   desc: "Cuando un plazo suyo está por vencer (2 h antes) o ya venció." },
];

function PreferencesModal({ open, onClose }) {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const [prefs, setPrefs] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      const initial = {};
      for (const opt of NOTIF_OPTIONS) {
        initial[opt.key] = user?.notification_prefs?.[opt.key] !== false;
      }
      setPrefs(initial);
    }
  }, [open, user]);

  function toggle(key) { setPrefs(p => ({ ...p, [key]: !p[key] })); }

  async function save() {
    setBusy(true);
    try {
      await usersApi.updatePreferences({ notification_prefs: prefs });
      toast.push("Preferencias guardadas");
      refreshUser?.();
      onClose();
    } catch {
      toast.push("Error al guardar preferencias", "error");
    } finally { setBusy(false); }
  }

  if (!open) return null;
  return (
    <Modal
      open={open}
      title="Preferencias de notificaciones"
      onClose={busy ? undefined : onClose}
      footer={<>
        <button className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
        <button disabled={busy} onClick={save}>{busy && <Spinner color="currentColor" />} Guardar</button>
      </>}
    >
      <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Desmarque para dejar de recibir ese tipo de notificación.
      </div>
      <div className="col" style={{ gap: 12 }}>
        {NOTIF_OPTIONS.map(opt => (
          <label key={opt.key} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
            <input type="checkbox" checked={!!prefs[opt.key]} onChange={() => toggle(opt.key)} style={{ marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>{opt.label}</div>
              <div className="muted" style={{ fontSize: 11 }}>{opt.desc}</div>
            </div>
          </label>
        ))}
      </div>
    </Modal>
  );
}
